import { useCallback } from 'react';

import { useAppRouterState } from '@/app/addRouter';

/** Does `path` point at `target` or somewhere below it? */
export function pathMatches(path: string, target: string): boolean {
  if (!target || target === '#') return false;
  const normalized = path.replace(/\/+$/, '') || '/';
  const wanted = target.replace(/\/+$/, '') || '/';
  if (wanted === '/') return normalized === '/';
  return normalized === wanted || normalized.startsWith(`${wanted}/`);
}

/**
 * A value that changes whenever the user navigates. Use it to react to "a
 * navigation happened", independently of where it went.
 */
export function useNavigationKey(): string {
  const { paths } = useAppRouterState();
  return paths.join('|');
}

/**
 * Tells whether a navigation target is currently open — in *either* stack,
 * since this app keeps two drill-down stacks in one URL (split on `/~`).
 *
 * TanStack's own `data-status="active"` can't be used for this: links are
 * rewritten to full two-stack URLs, so the router only ever sees an exact
 * match on the combined path.
 */
export function useIsPathActive(): (target?: string) => boolean {
  const { paths } = useAppRouterState();
  return useCallback(
    (target?: string) =>
      !!target && paths.some((path) => path && pathMatches(path, target)),
    [paths],
  );
}
